// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaUsageVO, ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { CompositeArticleUtils } from './composite-article-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { StringUtils } from '@shared/util/string-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleTypeConstants } from '../constants/article-type-constants';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { ArticleBehaviorPropertyDef } from '../constants/article-behavior-property-def';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleUsageService } from '../service/article-usage.service';

@Injectable({ providedIn: 'root' })
export class CodingArticleUtils {
  constructor(
    private _compositeArticleUtils: CompositeArticleUtils,
    private _articleManager: ArticleManager,
    private _localeResourceManager: LocaleResourceManager,
    private articleUsageService: ArticleUsageService
  ) {}

  public static hasCodingFingerConnection(article: ArticleVO): boolean {
    const codingFingerConnectionName: string = this.findCodingFingerConnectionName(article);
    return !!codingFingerConnectionName;
  }

  public static hasMultipleCodingFingerConnections(article: ArticleVO): boolean {
    return this.getCodingFingerConnectionCount(article) > 1;
  }

  public static getCodingFingerConnectionCount(article: ArticleVO): number {
    if (!article || ArrayUtils.isEmpty(article.metaElements)) {
      return 0;
    }
    let codingFingerCounter: number = 0;
    let i: number = 0;
    while (i <= article.metaElements.length) {
      const connectionAreaVO: ConnectionAreaVO = article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        if (
          PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE) ===
          ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE
        ) {
          codingFingerCounter++;
        }
      }
      i++;
    }
    return codingFingerCounter;
  }

  public static findConnectionsForType(article: ArticleVO, articleUsage: ArticleUsageVO, type: string): any[] {
    const result: any[] = [];
    if (!article || !article.metaElements) {
      return result;
    }
    let i: number = 0;
    while (i <= article.metaElements.length) {
      const connectionAreaVO: ConnectionAreaVO = article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === type) {
          const connection: any = {};
          connection.position = this.getPositionFromConnectionName(connectionAreaVO.name);
          connection.positionString = this.getPositionStringFromConnection(connectionAreaVO);
          connection.name = connectionAreaVO.name;
          connection.isInUse = ArticleUsageUtils.isConnectionInUse(articleUsage, connectionAreaVO.name);
          connection.removedCodingFingerArticleId = this.getRemovedCodingFingerArticleId(articleUsage, connectionAreaVO.name);
          if (connection.isInUse) {
            connection.connectedArticleUsageId = ArticleUsageUtils.getFirstConnectedArticleUsageIdOrEmptyByConnectionName(articleUsage, connectionAreaVO.name);
          } else {
            connection.connectedArticleUsageId = null;
          }
          result.push(connection);
        }
      }
      i++;
    }
    return result;
  }

  public static findCodingFingerConnectionName(article: ArticleVO, position: number = 1): string {
    if (!article || ArrayUtils.isEmpty(article.metaElements)) {
      return null;
    }
    let i: number = 0;
    while (i <= article.metaElements.length) {
      const connectionAreaVO: ConnectionAreaVO = article.metaElements[i] as ConnectionAreaVO;
      if (connectionAreaVO) {
        const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (typeName === ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE) {
          const positionFromConnectionName: number = this.getPositionFromConnectionName(connectionAreaVO.name);
          if (position === positionFromConnectionName) {
            return connectionAreaVO.name;
          }
        }
      }
      i++;
    }
    return null;
  }

  // TODO: (from flex) this might just be a temporary solution and could be replaced by a property with position info
  public static getPositionFromConnectionName(name: string): number {
    let pos: number = 1;
    if (name) {
      const subStrIndex: number = name.lastIndexOf('_');
      if (subStrIndex > 0) {
        const subString: string = name.substr(subStrIndex + 1);
        if (subString && subString.length > 0) {
          pos = parseInt(subString, 10);
          if (NumberUtils.isNotNumber(pos)) {
            pos = 1;
          }
        }
      }
    }
    return pos;
  }

  public static getPositionStringFromConnection(connectionAreaVO: ConnectionAreaVO, concat: string = '.'): string {
    let result: string = '';
    const positionArray: number[] = PropertyUtils.getIntPropertyValues(connectionAreaVO.behaviorProperties, MetaElementBehaviorPropertyDef.POSITION);
    if (ArrayUtils.isNotEmpty(positionArray)) {
      positionArray.reverse();
      while (positionArray.length > 0) {
        const pos: number = positionArray.pop();
        if (result.length > 0) {
          result = result + concat;
        }
        result = result + pos.toString();
      }
    }
    return result;
  }

  // coding finger is Kodiernase
  public static isCodingFinger(article: ArticleVO): boolean {
    if (!article || ArrayUtils.isEmpty(article.metaElements)) {
      return false;
    }
    if (ArticleUtils.isCodingFinger(article)) {
      return true;
    }
    // legacy check: coding fingers have exactly 1 connection
    if (article.metaElements.length !== 1) {
      return false;
    }
    const connectionAreaVO: ConnectionAreaVO = article.metaElements[0] as ConnectionAreaVO;
    if (connectionAreaVO) {
      const typeName: string = PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionFeatures, ArticleConnectionPropertyDef.TYPE);
      if (typeName === ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE) {
        return true;
      }
    }
    return false;
  }

  public static isCodingBolt(article: ArticleVO, articleUsage: ArticleUsageVO): boolean {
    if (article.type.displayName === ArticleTypeConstants.TYPE_CODING_KEY) {
      for (const connectionArea of ArticleUtils.connectionAreas(article)) {
        if (connectionArea.connectionSide === ConnectionAreaVO.SIDE_GIVING) {
          const connectionName: string = connectionArea.name;
          if (
            PropertyUtils.getStringPropertyValue(connectionArea.connectionFeatures, ArticleConnectionPropertyDef.TYPE) ===
              ArticleConnectionPropertyDef.CODING_BOLT_OPENING ||
            PropertyUtils.getStringPropertyValue(connectionArea.connectionFeatures, ArticleConnectionPropertyDef.TYPE) ===
              ArticleConnectionPropertyDef.CODING
          ) {
            // test if connection actually has a corresponding usage (otherwise the article CAN be a coding bolt
            // but is not actually used as one in the current case)
            const metaElementUsage = ArticleUsageUtils.findMetaElementUsageByName(articleUsage, connectionName);
            if (metaElementUsage) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  public static getCodingReference(connectedArticle: ArticleVO, connectedConnectionName: string): string {
    let runI: number = 0;
    let codingReference: string = null;
    while (runI <= connectedArticle.metaElements.length && !codingReference) {
      if (
        connectedArticle.metaElements[runI] instanceof ConnectionAreaVO &&
        (connectedArticle.metaElements[runI] as ConnectionAreaVO).name === connectedConnectionName
      ) {
        codingReference = PropertyUtils.getStringPropertyValue(
          connectedArticle.metaElements[runI].behaviorProperties,
          ArticleConnectionPropertyDef.CODING_REFERENCE
        );
        if (codingReference) {
          return codingReference;
        }
      }
      runI++;
    }
    return codingReference;
  }

  public static getCodingReferences(connectedArticle: ArticleVO, connectedConnectionName: string): string[] {
    let runI: number = 0;
    let codingReferences: string[] = [];
    while (runI <= connectedArticle.metaElements.length) {
      if (
        connectedArticle.metaElements[runI] instanceof ConnectionAreaVO &&
        (connectedArticle.metaElements[runI] as ConnectionAreaVO).name === connectedConnectionName
      ) {
        codingReferences = PropertyUtils.getStringPropertyValues(
          connectedArticle.metaElements[runI].behaviorProperties,
          ArticleConnectionPropertyDef.CODING_REFERENCE
        );
        if (ArrayUtils.isNotEmpty(codingReferences)) {
          return codingReferences;
        }
      }
      runI++;
    }
    return codingReferences;
  }

  public static getRemovedCodingFingerArticleId(articleUsage: ArticleUsageVO, metaElementName: string = ''): string {
    let result: string = '';
    if (metaElementName && metaElementName.length > 0) {
      result = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        articleUsage,
        metaElementName,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID
      );
    }
    if (!result || result.length < 1) {
      result = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID);
    }
    return result;
  }

  public static getRemovedCodingFingerArticleVersion(articleUsage: ArticleUsageVO, metaElementName: string = ''): string {
    let result: string = '';
    if (metaElementName && metaElementName.length > 0) {
      result = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        articleUsage,
        metaElementName,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION
      );
    }
    if (!result || result.length < 1) {
      result = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION
      );
    }
    return result;
  }

  public static getRemovedCodingFingerCompositePartUsageId(articleUsage: ArticleUsageVO, metaElementName: string = ''): string {
    let result: string = '';
    if (metaElementName && metaElementName.length > 0) {
      result = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        articleUsage,
        metaElementName,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID
      );
    }
    if (!result || result.length < 1) {
      result = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID
      );
    }
    return result;
  }

  public storeRemovedCodingFingerArticleInformation(
    articleUsage: ArticleUsageVO,
    condingFingerUsage: ArticleUsageVO,
    storeCompositeInformation: boolean = false
  ): void {
    const removedCodingFingerCounter: number = CodingArticleUtils.getRemovedCodingFingerCounter(articleUsage);
    ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID,
      condingFingerUsage.articleId.toString()
    );
    ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION,
      condingFingerUsage.articleVersion.toString()
    );
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COUNTER,
      removedCodingFingerCounter + 1
    );
    if (storeCompositeInformation) {
      const compositePartUsageId: string = this._compositeArticleUtils.getCompositePartUsageIdFromUsage(condingFingerUsage);
      if (compositePartUsageId) {
        ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
          articleUsage,
          ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID,
          compositePartUsageId
        );
      }
    }
  }

  public storeRemovedContactArticleInformation(
    articleUsage: ArticleUsageVO,
    contactUsage: ArticleUsageVO,
    storeCompositeInformation: boolean = false
  ): void {
    let firstConnectionAreaUsage = ArticleUsageUtils.tryGetFirstAsConnectionAreaUsage(contactUsage);

    if(firstConnectionAreaUsage) {
      const metaNameOnParentArticle: string = firstConnectionAreaUsage.connection.acceptingAreaId.metaElementName;

      const connectionAreaUsage = ArticleUsageUtils.tryGetConnectionAreaUsageByName(articleUsage, metaNameOnParentArticle);
      if (connectionAreaUsage) {
        ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
          articleUsage,
          metaNameOnParentArticle,
          ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID,
          contactUsage.articleId.toString()
        );
        ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
          articleUsage,
          metaNameOnParentArticle,
          ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION,
          contactUsage.articleVersion.toString()
        );
        if (storeCompositeInformation) {
          const compositePartUsageId: string = this._compositeArticleUtils.getCompositePartUsageIdFromUsage(contactUsage);
          if (compositePartUsageId) {
            ArticleUsageUtils.pushArticleUsageInfoPropertyStringValue(
              articleUsage,
              ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID,
              compositePartUsageId
            );
          }
        }
      }
    }
  }

  public static clearRemovedContactArticleInformation(
    articleUsage: ArticleUsageVO,
    contactArticleUsage: ArticleUsageVO,
    clearCompositeInformation: boolean = false
  ): void {
    const firstConnectionAreaUsage = ArticleUsageUtils.tryGetFirstAsConnectionAreaUsage(contactArticleUsage);
    if (firstConnectionAreaUsage) {
      const metaNameOnParentArticle: string = firstConnectionAreaUsage.connection.acceptingAreaId
        .metaElementName;
      ArticleUsageUtils.removeDefinedMetaElementInfoPropertyByMetaElementUsageAndPropertyName(
        articleUsage,
        metaNameOnParentArticle,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID
      );
      ArticleUsageUtils.removeDefinedMetaElementInfoPropertyByMetaElementUsageAndPropertyName(
        articleUsage,
        metaNameOnParentArticle,
        ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION
      );
      if (clearCompositeInformation) {
        ArticleUsageUtils.removeArticleUsageInfoProperty(articleUsage, ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID);
      }
    }
  }

  public static clearRemovedCodingFingerArticleInformation(articleUsage: ArticleUsageVO, clearCompositeInformation: boolean = false): void {
    let removedCodingFingerCounter: number = this.getRemovedCodingFingerCounter(articleUsage);
    if (removedCodingFingerCounter > 0) {
      removedCodingFingerCounter--;
    }
    if (removedCodingFingerCounter <= 0) {
      ArticleUsageUtils.removeArticleUsageInfoProperty(articleUsage, ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID);
      ArticleUsageUtils.removeArticleUsageInfoProperty(articleUsage, ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_VERSION);
    }
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COUNTER,
      removedCodingFingerCounter
    );
    if (clearCompositeInformation) {
      ArticleUsageUtils.removeArticleUsageInfoProperty(articleUsage, ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COMPOSITE_PART_USAGE_ID);
    }
  }

  public static getRemovedCodingFingerCounter(articleUsage: ArticleUsageVO): number {
    const existingId: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_ARTICLE_ID
    );
    let removedCodingFingerCounter: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.REMOVED_CODING_FINGER_COUNTER
    );
    if (existingId && existingId.length > 0) {
      if (isNaN(removedCodingFingerCounter)) {
        removedCodingFingerCounter = 1;
      }
    } else {
      if (isNaN(removedCodingFingerCounter)) {
        removedCodingFingerCounter = 0;
      }
    }
    return removedCodingFingerCounter;
  }

  public searchCodingReferencesForConnectionLeadingToCodingFinger(
    bridgeArticleUsage: ArticleUsageVO,
    bridgeArticleVO: ArticleVO,
    connectionName: string,
    connectionName2: string = null
  ): ConnectionAreaUsageVO {
    if (!bridgeArticleUsage || !bridgeArticleVO || !connectionName) {
      return null;
    }
    const potentialConnectionsLeadingToCodingNose: ConnectionAreaUsageVO[] = [];
    let connectionLeadingToCodingNose: ConnectionAreaUsageVO = null;
    for (const metaElementUsage of bridgeArticleUsage.metaElementUsages) {
      const backwardCodingRefs: string[] = CodingArticleUtils.getCodingReferences(bridgeArticleVO, metaElementUsage.identity.metaElementName);
      if (ArrayUtils.isNotEmpty(backwardCodingRefs)) {
        let c1Found: boolean = false;
        let c2Found: boolean = !connectionName2 ? true : false;
        for (const bwRef of backwardCodingRefs) {
          if (bwRef === connectionName) {
            c1Found = true;
          } else if (!c2Found && bwRef === connectionName2) {
            c2Found = true;
          }
        }
        if (c1Found && c2Found) {
          potentialConnectionsLeadingToCodingNose.push(metaElementUsage as ConnectionAreaUsageVO);
        }
      }
    }
    if (potentialConnectionsLeadingToCodingNose.length === 1) {
      connectionLeadingToCodingNose = potentialConnectionsLeadingToCodingNose.pop();
    } else if (potentialConnectionsLeadingToCodingNose.length === 2) {
      connectionLeadingToCodingNose = potentialConnectionsLeadingToCodingNose.pop();
      if (ConnectionInfoUtils.isGivingConnection(bridgeArticleVO, connectionLeadingToCodingNose.identity.metaElementName)) {
        connectionLeadingToCodingNose = potentialConnectionsLeadingToCodingNose.pop();
      }
    }
    return connectionLeadingToCodingNose;
  }

  public getCodingFingerList(
    compositePartUsages: ArticleUsageVO[],
    compositeArticleVO: ArticleVO,
    type: string,
    presenter: ProjectContentPresenter = null
  ): any[] {
    let useCounter: boolean = false;
    const codingFingerList: any[] = [];
    for (const compositePartUsage of compositePartUsages) {
      const compositePartArticleVO: ArticleVO = this._articleManager.getCachedArticle(compositePartUsage.articleId, compositePartUsage.articleVersion);

      if (this.getAttachCodingfingerAlternativeArticle(compositePartUsage)) {
        const codingFingerOccurence: any = {};
        codingFingerOccurence.codingFinger = this._localeResourceManager.getValueByKey('settingsTabNavigator.label.codingFingerRemoved');
        codingFingerOccurence.isInUse = false;
        codingFingerOccurence.articleUsageId = compositePartUsage.id;
        if (presenter) {
          codingFingerOccurence.positionLabel = presenter.getPositionLabelByArticleUsageId(compositePartUsage.id);
        }
        codingFingerList.push(codingFingerOccurence);
        continue;
      }

      if (!compositePartArticleVO) {
        continue;
      }
      if (
        type === ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE ||
        CodingArticleUtils.hasMultipleCodingFingerConnections(compositePartArticleVO)
      ) {
        const codingFingers = CodingArticleUtils.findConnectionsForType(compositePartArticleVO, compositePartUsage, type);
        for (const codingFinger of codingFingers) {
          const codingFingerOccurence: any = new Object();
          if (codingFinger.isInUse) {
            codingFingerOccurence.codingFinger = this._localeResourceManager.getValueByKey('settingsTabNavigator.label.codingFingerIntact');
            codingFingerOccurence.isInUse = true;
          } else {
            codingFingerOccurence.codingFinger = this._localeResourceManager.getValueByKey('settingsTabNavigator.label.codingFingerRemoved');
            codingFingerOccurence.isInUse = false;
          }
          codingFingerOccurence.removedCodingFingerArticleId = codingFinger.removedCodingFingerArticleId;
          codingFingerOccurence.name = codingFinger.name;
          codingFingerOccurence.articleUsageId = compositePartUsage.id;
          codingFingerOccurence.connectedArticleUsageId = codingFinger.connectedArticleUsageId;
          codingFingerOccurence.corridor = codingFinger.position;
          codingFingerOccurence.positionLabel = codingFinger.positionString;
          codingFingerList.push(codingFingerOccurence);
        }
      } else if (CodingArticleUtils.hasCodingFingerConnection(compositePartArticleVO)) {
        useCounter = true;
        const codingFingerOccurence: any = new Object();
        if (CodingArticleUtils.getRemovedCodingFingerArticleId(compositePartUsage)) {
          codingFingerOccurence.codingFinger = this._localeResourceManager.getValueByKey('settingsTabNavigator.label.codingFingerRemoved');
          codingFingerOccurence.removedCodingFingerArticleId = CodingArticleUtils.getRemovedCodingFingerArticleId(compositePartUsage);
          codingFingerOccurence.isInUse = false;
        } else {
          codingFingerOccurence.codingFinger = this._localeResourceManager.getValueByKey('settingsTabNavigator.label.codingFingerIntact');
          codingFingerOccurence.isInUse = true;
        }
        const compositeGivingConnectionName: string = PropertyUtils.getStringPropertyValue(
          compositePartUsage.infoProperties,
          ArticleUsageInfoPropertyDef.COMPOSITE_GIVING_CONNECTION_NAME
        );
        let corridor: number = 0;
        if (compositeArticleVO && compositeArticleVO.metaElements && !StringUtils.isEmpty(compositeGivingConnectionName)) {
          for (const metaElementVO of compositeArticleVO.metaElements) {
            if (metaElementVO.name === compositeGivingConnectionName) {
              corridor = metaElementVO.corridor;
            }
          }
        }
        let articlePosition: string = null;
        if (presenter) {
          articlePosition = presenter.getPositionLabelByArticleUsageId(compositePartUsage.id);
        }
        if (!StringUtils.isEmpty(articlePosition)) {
          codingFingerOccurence.positionLabel = articlePosition;
          useCounter = false;
        } else {
          codingFingerOccurence.positionLabel = corridor.toString();
        }
        codingFingerOccurence.name = null;
        codingFingerOccurence.articleUsageId = compositePartUsage.id;
        codingFingerOccurence.corridor = corridor;

        codingFingerList.push(codingFingerOccurence);
      }
    }
    let sortFieldName: string = 'corridor';
    if (type === ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE || !useCounter) {
      sortFieldName = 'positionLabel';
    }
    // TODO: (from flex) hanni/DL: Maybe primarily (or only) sort by positionLabel?
    // sort.fields = [new SortField("positionLabel", true, false, true)];
    codingFingerList.sort((a: any, b: any) => NumberUtils.numericCompare(+a[sortFieldName], +b[sortFieldName]));
    // Relabel from 1 to x when corridor positions where used
    if (useCounter) {
      let counter: number = 1;
      while (counter <= codingFingerList.length) {
        codingFingerList[counter - 1].positionLabel = counter;
        counter++;
      }
    }

    return codingFingerList;
  }

  public getDetachCodingfingerAlternativeArticle(articleUsage: ArticleUsageVO): number {
    const article = this.articleUsageService.getArticleOrCompositeArticleByArticleUsage(articleUsage);
    const alternativeArticle = PropertyUtils.getIntPropertyValue(
      article.connectionFeatures,
      ArticleBehaviorPropertyDef.ALTERNATIVE_WITHOUT_CODINGFINGER
    );
    return alternativeArticle;
  }

  public getAttachCodingfingerAlternativeArticle(articleUsage: ArticleUsageVO): number | null {
    const article = this.articleUsageService.getArticleOrCompositeArticleByArticleUsage(articleUsage);
    if(!article) {
      return null;
    }
    const alternativeArticle = PropertyUtils.getIntPropertyValue(
      article.connectionFeatures,
      ArticleBehaviorPropertyDef.ALTERNATIVE_WITH_CODINGFINGER
    );
    return alternativeArticle;
  }
}
